import type { ColunasDeEntrega } from "@/components/prosthesis/endereco-de-entrega";
import { situacaoDoPrazo, type PrazoDoLaboratorio, type ProsthesisStatus, type SituacaoDoPrazo } from "@bookdental/schemas";
import { TERMINAL_STATUSES } from "@/components/prosthesis/prosthesis-meta";
import type { AcrescimoDoPedidoRow, ProsthesisServiceRow } from "@/components/prosthesis/types";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

export type IncomingOrderRow = ColunasDeEntrega & {
  id: string;
  /** Número sequencial do pedido no laboratório (1042). */
  numero?: number | null;
  patientName: string;
  organization: { name: string; isPersonal: boolean };
  dentistName: string | null;
  description: string | null;
  deadline: Date | string | null;
  createdAt: Date | string;
  status: ProsthesisStatus;
  sentChannels: string[];
  services: ProsthesisServiceRow[];
  acrescimos?: AcrescimoDoPedidoRow[];
};

export type ProductionViewMode = "estagios" | "planilha";

export type ServiceRow = { order: IncomingOrderRow; service: ProsthesisServiceRow };

/** Achata pedidos em linhas de serviço (1 linha por serviço) — pedido sem serviço vira 1 linha vazia. */
export function flattenServiceRows(orders: IncomingOrderRow[]): ServiceRow[] {
  return orders.flatMap((order) =>
    order.services.length > 0
      ? order.services.map((service) => ({ order, service }))
      : [{ order, service: null as unknown as ProsthesisServiceRow }],
  );
}

export function serviceLabel(service: ProsthesisServiceRow | null): string {
  if (!service) return "—";
  return service.teeth ? `${service.serviceCategory.label} (${service.teeth})` : service.serviceCategory.label;
}

/** Resumo compacto pra linha de lista — serviço único mostra o rótulo completo, mais de um vira
 *  contagem (evita linha longa demais em telas de resumo como a Início do laboratório). */
export function orderServicesSummary(order: IncomingOrderRow): string {
  if (order.services.length === 0) return "—";
  if (order.services.length === 1) return serviceLabel(order.services[0]);
  return `${order.services.length} serviços`;
}

/** Atrasado no nível do serviço — mesma regra do isOverdue (dashboard-lab.ts), mas olhando o status
 *  do próprio serviço (não o rollup do pedido): um serviço já concluído não fica marcado por causa
 *  de um prazo vencido de outro serviço do mesmo pedido. */
export function isServiceOverdue(row: ServiceRow, prazo: PrazoDoLaboratorio): boolean {
  return situacaoDoServico(row, prazo) === "atrasado";
}

/** Atrasado, vence em breve (janela do aviso do laboratório) ou no prazo — pelo serviço. Serviço
 *  concluído ou cancelado fica sempre "no prazo". */
export function situacaoDoServico(row: ServiceRow, prazo: PrazoDoLaboratorio): SituacaoDoPrazo {
  if (!row.service || TERMINAL_STATUSES.includes(row.service.status)) return "noPrazo";
  return situacaoDoPrazo(row.order, prazo);
}

/** Serviços (preço × quantidade) + acréscimos do pedido. */
export function orderTotalCents(order: IncomingOrderRow): number {
  return totalParaExibir(order.services, order.acrescimos) ?? 0;
}

/** Pedido ainda sem orçamento — nenhum serviço tem preço definido. Nesse caso R$ 0,00 confundiria
 *  com "gratuito"; a tela deve exibir "—" em vez do total. */
export function orderIsPriced(order: IncomingOrderRow): boolean {
  return order.services.some((s) => s.priceCents != null);
}

/** Materiais únicos usados no pedido (pode ter mais de um serviço) — "—" se nenhum informado.
 *  Prioriza a categoria química (modo simples atual); cai pra linha de marca em pedidos antigos. */
export function orderMaterialsSummary(order: IncomingOrderRow): string {
  const names = [
    ...new Set(order.services.map((s) => rotuloDoMaterialDoServico(s)).filter(Boolean)),
  ] as string[];
  return names.length ? names.join(" · ") : "—";
}

/** Cores/tonalidades únicas usadas no pedido — "—" se nenhuma informada. */
export function orderColorsSummary(order: IncomingOrderRow): string {
  const codes = [...new Set(order.services.map((s) => s.shadeOption?.code).filter(Boolean))] as string[];
  return codes.length ? codes.join(" · ") : "—";
}
